// The Parliament chapter's calls to Loggione, as TanStack Query hooks. The
// components never see the api: a page calls a hook here and hands the
// result to src/features/parliament/adapt.ts before it reaches a component.
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { api, unwrap } from "../../api/client";
import { keys } from "../../api/keys";
import type { Origin } from "../news/adapt";
import type { Chamber, SessionType, Sitting, SittingDetail } from "./adapt";

/**
 * How often a query of the chapter asks again: a number of milliseconds,
 * false (never), or a function of what it has, read again at every render
 * (so a list of sittings can decide from the other lists too).
 */
export type Polling<T> = number | false | ((data: T) => number | false);

/** How long an index stays fresh: as the item's detail it belongs to (useItem), so the two agree. */
const SECTIONS_STALE_TIME = 5 * 60_000;

/**
 * The index of a document, every entry (GET /items/{origin}/{item_id}/sections):
 * [] for a News item and for a record whose index has not arrived. Asked with
 * the item's detail and kept as long: the offsets hold on the text served
 * with it. Not asked again when the window gets the focus.
 */
export function useSections(origin: Origin, id: string, enabled = true) {
  return useQuery({
    queryKey: keys.sections(origin, id),
    queryFn: async () =>
      unwrap(
        (await api().GET("/api/v1/items/{origin}/{item_id}/sections", { params: { path: { origin, item_id: id } } }))
          .data,
      ),
    staleTime: SECTIONS_STALE_TIME,
    refetchOnWindowFocus: false,
    enabled,
  });
}

export const SITTINGS_PAGE_SIZE = 20;

/**
 * One list of sittings (GET /parliament/sittings), paged by cursor, newest
 * day first; `chamber` and `sessionType` null mean both / every type. Asked
 * again every `polling` ms while the window is in the foreground (never in
 * the background, never on a hidden tab): `polling` is the interval, or a
 * function of the sittings loaded (pollingInterval). Every page loaded is
 * refetched together, so the list never mixes two moments. When the filter
 * changes (another type ticked) the rows of the last filter stand in until
 * the new ones arrive (placeholder data), as the lists of news do.
 */
export function useSittings(chamber: Chamber | null, sessionType: SessionType | null, polling: Polling<Sitting[]>) {
  return useInfiniteQuery({
    queryKey: keys.sittings(chamber, sessionType),
    queryFn: async ({ pageParam }) =>
      unwrap(
        (
          await api().GET("/api/v1/parliament/sittings", {
            params: {
              query: {
                limit: SITTINGS_PAGE_SIZE,
                ...(chamber ? { chamber } : {}),
                ...(sessionType ? { session_type: sessionType } : {}),
                ...(pageParam ? { cursor: pageParam } : {}),
              },
            },
          })
        ).data,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
    refetchInterval:
      typeof polling === "function"
        ? (query) => polling(query.state.data?.pages.flatMap((page) => page.items) ?? [])
        : polling,
    refetchIntervalInBackground: false,
  });
}

/**
 * One sitting with its documents (GET /parliament/sittings/{chamber}/{session_type}/{number}),
 * asked again as `polling` says, in the foreground only, as the lists; a 404
 * is the page's «Seduta non trovata».
 */
export function useSitting(
  key: { chamber: Chamber; sessionType: SessionType; number: number },
  polling: Polling<SittingDetail | undefined>,
) {
  return useQuery({
    queryKey: keys.sitting(key.chamber, key.sessionType, key.number),
    queryFn: async () =>
      unwrap(
        (
          await api().GET("/api/v1/parliament/sittings/{chamber}/{session_type}/{number}", {
            params: { path: { chamber: key.chamber, session_type: key.sessionType, number: key.number } },
          })
        ).data,
      ),
    refetchInterval: typeof polling === "function" ? (query) => polling(query.state.data) : polling,
    refetchIntervalInBackground: false,
  });
}

/**
 * The reduced index of a sitting (GET /parliament/sittings/{chamber}/{session_type}/{number}/sections):
 * the parts, topics and items of every document, for the page of the
 * sitting (L2). Keyed with the sitting's `updated_at`, so a sitting that
 * changed asks its index again while the last one stands in (placeholder
 * data); kept as long as a document's index (SECTIONS_STALE_TIME), never
 * asked again when the window gets the focus. `enabled` false (the sitting
 * not yet known) asks nothing.
 */
export function useSittingIndex(
  key: { chamber: Chamber; sessionType: SessionType; number: number },
  updatedAt: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.sittingIndex(key.chamber, key.sessionType, key.number, updatedAt),
    queryFn: async () =>
      unwrap(
        (
          await api().GET("/api/v1/parliament/sittings/{chamber}/{session_type}/{number}/sections", {
            params: { path: { chamber: key.chamber, session_type: key.sessionType, number: key.number } },
          })
        ).data,
      ),
    staleTime: SECTIONS_STALE_TIME,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
  });
}
